import FlightDetailsOffersWrapper from '@/src/components/common/srp/flightDetailsOffers'
import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary/ErrorBoundary'
import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import { useFlightDetails } from '@/src/hooks/srp/flightDetails'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { getErrorMessage } from '@/src/utils/srp/searchUtils/getErrorMessage'
import { KeyboardArrowDownRounded } from '@mui/icons-material'
import { Box } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'
import FareRuleSection from '../../../../common/srp/flightDetails/FareRuleSections'
import AirportDetailsSections from './AirportDetailsSection'
import FlightDetailsFooter from './flightDetailsFooter'
import FlightDetailsHeader from './flightDetailsHeader'
import FlightDetailsSection from './flightDetailsSection'
import TripInfo from './tripInfo'

interface FlightDetailsProps {
    onClose: () => void
    flights: FlightCard[]
    selectedFareFamilyPlans?: (Plan | null)[]
    jsons: JsonsData
    sectors: Sectors
    onSelect: () => void
    sid: string
    showBaggage?: boolean
    isRTSolutionView?: boolean
    isBookingInProgress?: boolean
    loadingFlightKey?: string | null
}

export type FlightDetailsTabKey = 'flightDetails' | 'fareRules' | 'airportDetails'

const FlightDetailsSlider: React.FC<FlightDetailsProps> = ({
    onClose,
    flights,
    selectedFareFamilyPlans,
    jsons,
    sectors,
    onSelect,
    sid,
    showBaggage = true,
    isRTSolutionView,
    isBookingInProgress,
    loadingFlightKey
}) => {
    const {
        baggageData,
        baggageRTKError,
        baggageLoading,
        refetchBaggageInfo,
        minirule = [],
        miniRuleLoading,
        miniRuleRTKError,
        refetchMiniRulesInfo,
        prices,
        footerButtonCTA,
        retryCount,
        handleRetryBaggage,
        maxRetries,
        applicableCouponsData
    } = useFlightDetails({
        flights,
        selectedFareFamilyPlans,
        jsons,
        sectors,
        onSelect,
        sid,
        showBaggage,
        isRTSolutionView
    })
    const [selectedDetails, setSelectedDetails] = useState<FlightDetailsTabKey>('flightDetails')
    const [flightDetailsTracked, setFlightDetailsTracked] = useState(false)
    const [hasOffers, setHasOffers] = useState(false)
    const [isOffersInView, setIsOffersInView] = useState(false)
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const offersContainerRef = useRef<HTMLDivElement | null>(null)
    const { triggerSrpEvent } = useClevertapForSrp()
    const { cards, isMulticity } = useSearchResults()

    useEffect(() => {
        if (selectedDetails === 'fareRules' && miniRuleRTKError) {
            refetchMiniRulesInfo()
        }
    }, [selectedDetails, refetchMiniRulesInfo])
    useEffect(() => {
        if (baggageData && !flightDetailsTracked) {
            const cardIndex = cards?.[0]?.findIndex(card => card.id === flights[0].id) ?? -1

            // Extract baggage info from API response
            const deptStr = Object.keys(baggageData?.BI || {})[0]
            const baggage = baggageData?.BI?.[deptStr]?.bg?.ADT
            const checkingBaggage = baggage?.cib || 'N/A'
            const cabinBaggage = baggage?.cab || 'N/A'

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'srp_flight_details_view',
                flightNumber: flights[0].airlineCodes?.join('-') || 'N/A',
                checking_baggage: checkingBaggage,
                cabin_baggage: cabinBaggage,
                card_index: cardIndex + 1,
                action_type: 'Link',
                action_value: 'N/A'
            })
            setFlightDetailsTracked(true)
        }
    }, [baggageData, flightDetailsTracked, flights, cards, triggerSrpEvent])

    useEffect(() => {
        if (!hasOffers || selectedDetails !== 'flightDetails') {
            setIsOffersInView(false)
            return
        }
        if (!scrollContainerRef.current || !offersContainerRef.current) return

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    setIsOffersInView(entry.isIntersecting)
                })
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '50px',
                threshold: 0.1
            }
        )

        observer.observe(offersContainerRef.current)

        return () => {
            observer.disconnect()
        }
    }, [hasOffers, selectedDetails])

    const baggageError = getErrorMessage(baggageRTKError)
    const miniRuleError = getErrorMessage(miniRuleRTKError)
    const t = useTranslations('srpFlightDetails')
    const headings: { key: FlightDetailsTabKey; label: string }[] = [
        { key: 'flightDetails', label: t('flightDetails') },
        { key: 'fareRules', label: t('fareRules') }
        // { key: 'airportDetails', label: t('airportDetails') } // Disabled airport details for now
    ]

    const showOffersNudge = selectedDetails === 'flightDetails' && hasOffers && !isOffersInView

    return (
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'background.paper' }}>
                <ErrorBoundaryWrapper identifier="FlightDetailsHeader-desktop-srp">
                    <FlightDetailsHeader
                        onClose={onClose}
                        headings={headings}
                        selectedDetails={selectedDetails}
                        setSelectedDetails={tab => {
                            if (tab === 'fareRules' && selectedDetails !== 'fareRules') {
                                const cardIndex = cards?.[0]?.findIndex(card => card.id === flights[0].id) ?? -1
                                triggerSrpEvent('Air_UI_Action', {
                                    action_name: 'fare_rule_click',
                                    flightNumber: flights[0].airlineCodes?.join('-') || 'N/A',
                                    card_index: cardIndex + 1,
                                    action_type: 'Link',
                                    action_value: 'N/A'
                                })
                            }
                            setSelectedDetails(tab)
                        }}
                    />
                </ErrorBoundaryWrapper>
            </Box>

            <Box
                ref={scrollContainerRef}
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                }}
            >
                {flights.map((flight, flightIndex) => {
                    return (
                        <Box sx={{ mb: flightIndex === flights?.length - 1 ? 'auto' : '0' }} key={flight.id}>
                            {flight.sectorKeys.map((sectorKey, index) => {
                                const sector = sectors[sectorKey]
                                const layOverDurationMap = sector?.layoverDurationMap || []
                                return (
                                    <Box key={index}>
                                        <ErrorBoundaryWrapper identifier="flightDetails-desktop-srp-TripInfo">
                                            <TripInfo
                                                flight={flight}
                                                jsons={jsons}
                                                sector={sector}
                                                sectorIndex={index}
                                                flightIndex={
                                                    flights.length === 1
                                                        ? 0
                                                        : flights.length <= 2
                                                            ? flightIndex + 1
                                                            : -1
                                                }
                                            />
                                        </ErrorBoundaryWrapper>
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                height: '100%',
                                                justifyContent: 'space-between',
                                                overflow: 'auto'
                                            }}
                                        >
                                            {selectedDetails === 'flightDetails' && (
                                                <ErrorBoundaryWrapper identifier="flightDetails-desktop-srp-FlightDetailsSection">
                                                    <FlightDetailsSection
                                                        sector={sector}
                                                        jsons={jsons}
                                                        layOverDurationMap={layOverDurationMap}
                                                        baggageData={baggageData}
                                                        loading={baggageLoading}
                                                        error={baggageError}
                                                        showBaggage={showBaggage}
                                                        retryCount={retryCount}
                                                        handleRetryBaggage={handleRetryBaggage}
                                                        maxRetries={maxRetries}
                                                    />
                                                </ErrorBoundaryWrapper>
                                            )}
                                            {selectedDetails === 'fareRules' && (
                                                <ErrorBoundaryWrapper identifier="flightDetails-desktop-srp-FareRuleSection">
                                                    <FareRuleSection
                                                        miniruleData={minirule[isMulticity ? index : flightIndex] || {}}
                                                        loading={miniRuleLoading}
                                                        error={miniRuleError}
                                                    />
                                                </ErrorBoundaryWrapper>
                                            )}
                                            {selectedDetails === 'airportDetails' && <AirportDetailsSections />}
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>
                    )
                })}
                {selectedDetails === 'flightDetails' && (
                    <Box px={2}>
                        <FlightDetailsOffersWrapper
                            tags={applicableCouponsData?.tags?.tags || []}
                            offersContainerRef={offersContainerRef}
                            onOffersCountChange={count => setHasOffers(count > 0)}
                        />
                    </Box>
                )}
            </Box>
            {showOffersNudge && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 84,
                        transform: 'translateX(-50%)',
                        zIndex: 11,
                        bgcolor: '#ffc621',
                        color: '#1a1a1a',
                        borderRadius: '999px',
                        height: '42px',
                        px: 3,
                        py: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        boxShadow: '0px 4px 10px rgba(0,0,0,0.18)',
                        pointerEvents: 'none'
                    }}
                >
                    <Box component="span" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>
                        {t('specialOffers')}
                    </Box>
                    <KeyboardArrowDownRounded sx={{ fontSize: 30 }} />
                </Box>
            )}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 10,
                    backgroundColor: 'background.paper',
                    borderTop: 1,
                    borderColor: 'divider'
                }}
            >
                <ErrorBoundaryWrapper identifier="FlightDetailsFooter-desktop-srp">
                    <FlightDetailsFooter
                        price={prices.price}
                        strikeOffPrice={prices.strikeOffPrice}
                        onSelect={onSelect}
                        showSelectButton={true}
                        buttonCTA={footerButtonCTA}
                        isBookingInProgress={isBookingInProgress}
                        isButtonDisabled={
                            !!loadingFlightKey && flights?.[0]?.priceBreakup?.fare?.fk !== loadingFlightKey
                        }
                    />
                </ErrorBoundaryWrapper>
            </Box>
        </Box>
    )
}

export default FlightDetailsSlider
